/**
 * newCopy entries for CC send errors.
 */
import React from 'react';
import { CopyPack, Issue, IssueKind } from '../types';
import {
  ccNoRetryStep,
  ctxOf,
  dataOfFamily,
  HELP_ARTICLES,
  HelpArticle,
  importOrEditProfileSteps,
  personLabel,
  roleLabel,
  tt
} from './shared';

const dataOf = dataOfFamily('CC');

const ccBounced__howToFixIt = (g: Issue[]) => {
  const c = ctxOf(g);
  return (
    <ul>
      <li>{tt(c, 'If this actor is not required for all experiences, click ignore for those experiences')}</li>
      <li>
        {tt(c, 'If this does not work, confirm that this address is correct. If you need to update the email, you can do so by either:')}
        <ul>{importOrEditProfileSteps()}</ul>
      </li>
      <li>
        {tt(
          c,
          'If this is a work email address, also check that this account is active and is configured to accept email from senders outside of your organization.'
        )}
      </li>
      {ccNoRetryStep()}
    </ul>
  );
};

export const ccNewCopy = {
  [IssueKind.CC__FIXED_EMAIL_BOUNCED]: {
    category: 'Email bounced',
    summary: (g) =>
      tt(ctxOf(g), `Email(s) couldn't be sent: {{ emailAddress }} bounced`, { emailAddress: dataOf(g).email }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      return (
        <>
          {tt(c, '{{ emailAddress }} is supposed to receive a copy of these task(s)/message(s).', {
            emailAddress: d.email
          })}{' '}
          {tt(
            c,
            'When Dado tried to send an email to this address, it bounced. This can happen if the email address is incorrect; the account is inactive; the inbox is full; or the address is not configured to accept email from external senders.'
          )}
        </>
      );
    },
    howToFixIt: ccBounced__howToFixIt
  },
  [IssueKind.CC__NO_ACTOR]: {
    category: 'Email undeliverable',
    summary: (g) =>
      tt(ctxOf(g), `Email(s) couldn't be sent: "{{ actor }}" not assigned`, {
        actor: roleLabel(ctxOf(g), dataOf(g).recipientId)
      }),
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      return (
        <>
          {tt(c, 'The')} <strong>{roleLabel(c, d.recipientId)}</strong>{' '}
          {tt(c, 'role is supposed to receive a copy of these task(s)/message(s).')}{' '}
          {tt(c, "When Dado tried to send the timeline item(s) listed below, nobody was assigned in this role in these experience(s), so it couldn't be sent.")}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>{tt(c, 'If this actor is not required for all experiences, click ignore for those experiences')}</li>
          <li>
            {tt(
              c,
              'Click the participants listed below to open up each experience, and check that the role is now assigned'
            )}
          </li>
          <li>
            {tt(c, 'If not, assign a user to this role')} <HelpArticle href={HELP_ARTICLES.changeStartDateOrManager} />
          </li>
          {ccNoRetryStep()}
        </ul>
      );
    }
  },
  [IssueKind.CC__NO_EMAIL]: {
    category: 'Email undeliverable',
    summary: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      return tt(c, `Email(s) couldn't be sent to {{ fullName }}: no work email in Dado`, {
        fullName: personLabel(c, d.recipientId, d.instance)
      });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      return (
        <>
          {tt(c, 'The')} <strong>{roleLabel(c, d.recipientId)}</strong>{' '}
          {tt(c, 'role is supposed to receive a copy of these task(s)/message(s).')}{' '}
          {tt(c, "When Dado tried to send the timeline item(s) listed below, {{ fullName }} didn't have a work email set in Dado, so it couldn't be sent.", {
            fullName: personLabel(c, d.recipientId, d.instance)
          })}
        </>
      );
    },
    howToFixIt: (g) => {
      const c = ctxOf(g);
      return (
        <ul>
          <li>{tt(c, 'If this actor is not required for all experiences, click ignore for those experiences')}</li>
          <li>
            {tt(c, 'If this does not work, add a work email by either:')}
            <ul>{importOrEditProfileSteps('work email')}</ul>
          </li>
          {ccNoRetryStep()}
        </ul>
      );
    }
  },
  [IssueKind.CC__ROLE_EMAIL_BOUNCED]: {
    category: 'Email bounced',
    summary: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      return tt(c, `Email(s) couldn't be sent to {{ fullName }}: {{ emailAddress }} bounced`, {
        fullName: personLabel(c, d.recipientId, d.instance),
        emailAddress: d.email
      });
    },
    whatCausedIt: (g) => {
      const c = ctxOf(g);
      const d = dataOf(g);
      return (
        <>
          {tt(c, 'The')} <strong>{roleLabel(c, d.recipientId)}</strong>{' '}
          {tt(c, 'role is supposed to receive a copy of these task(s)/message(s).')}{' '}
          {tt(
            c,
            'When Dado tried to send an email to their email address ({{ emailAddress }}), it bounced. This can happen if the email address is incorrect; the account is inactive; the inbox is full; or the address is not configured to accept email from external senders.',
            { emailAddress: d.email }
          )}
        </>
      );
    },
    howToFixIt: ccBounced__howToFixIt
  }
} satisfies Partial<CopyPack>;
